import React, { useEffect, useRef, useState } from 'react';

export default function YouTubePlayer({ videoId }) {
  const width = 960;
  const height = 540; // 16:9 aspect ratio
  const playerRef = useRef(null);
  const [player, setPlayer] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(50);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [availableQualities, setAvailableQualities] = useState([]);
  const [currentQuality, setCurrentQuality] = useState('auto');
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  // Load YouTube API
  useEffect(() => {
    if (!window.YT) {
      const script = document.createElement('script');
      script.src = 'https://www.youtube.com/iframe_api';
      document.body.appendChild(script);
    }

    window.onYouTubeIframeAPIReady = () => {
      initializePlayer();
    };

    if (window.YT && window.YT.Player) {
      initializePlayer();
    }

    return () => {
      if (player) {
        player.destroy();
      }
    };
  }, [videoId]);

  const initializePlayer = () => {
    const ytPlayer = new window.YT.Player(playerRef.current, {
      height: height,
      width: width,
      videoId: videoId,
      playerVars: {
        controls: 0, // Hide YouTube controls
        modestbranding: 1,
        rel: 0,
        showinfo: 0,
        fs: 1,
        iv_load_policy: 3,
        disablekb: 1,
        autoplay: 1 // Enable autoplay
      },
      events: {
        onReady: (event) => {
          setPlayer(event.target);
          setVolume(event.target.getVolume());
          // Get qualities after a short delay to ensure they're loaded
          setTimeout(() => {
            const qualities = event.target.getAvailableQualityLevels();
            console.log('Available qualities:', qualities);
            setAvailableQualities(qualities);
            // Don't automatically set quality - let user control it
          }, 1000);
        },
        onStateChange: (event) => {
          setIsPlaying(event.data === window.YT.PlayerState.PLAYING);
          // Only try to get qualities if we don't have any yet
          if (event.data === window.YT.PlayerState.PLAYING && availableQualities.length === 0) {
            const qualities = event.target.getAvailableQualityLevels();
            if (qualities.length > 0) {
              console.log('Available qualities on play:', qualities);
              setAvailableQualities(qualities);
              // Don't set current quality - let user control it
            }
          }
        }
      }
    });
  };

  const togglePlayPause = () => {
    if (player) {
      if (isPlaying) {
        player.pauseVideo();
      } else {
        player.playVideo();
      }
    }
  };

  

  const handleVolumeChange = (newVolume) => {
    if (player) {
      player.setVolume(newVolume);
      setVolume(newVolume);
    }
  };

  const handleSpeedChange = (speed) => {
    if (player) {
      player.setPlaybackRate(speed);
      setPlaybackRate(speed);
    }
  };

  const handleQualityChange = (quality) => {
    if (player) {
      console.log('Changing quality to:', quality);
      if (quality === 'auto') {
        // For auto, set to default and let YouTube decide
        player.setPlaybackQuality('default');
      } else {
        // For specific quality, set it directly
        player.setPlaybackQuality(quality);
      }
      setCurrentQuality(quality);
      console.log('Quality selection set to:', quality);
    }
  };

  // Update current time and duration periodically
  useEffect(() => {
    if (!player) return;

    // set initial duration
    try {
      const d = player.getDuration() || 0;
      setDuration(d);
    } catch (err) {}

    const id = setInterval(() => {
      try {
        const t = player.getCurrentTime();
        const d = player.getDuration() || 0;
        setCurrentTime(t);
        setDuration(d);
      } catch (err) {
        // ignore until player is ready
      }
    }, 500);

    return () => clearInterval(id);
  }, [player]);

  const formatTime = (secs) => {
    if (!secs || isNaN(secs) || secs === Infinity) return '0:00';
    const s = Math.floor(secs % 60).toString().padStart(2, '0');
    const m = Math.floor(secs / 60);
    return `${m}:${s}`;
  };

  const handleSeek = (e) => {
    if (!player) return;
    const pct = parseFloat(e.target.value);
    const seekToSeconds = (pct / 100) * duration;
    player.seekTo(seekToSeconds, true);
    setCurrentTime(seekToSeconds);
  };

  // Full overlay style to prevent any clicking on the video
  const fullVideoOverlayStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'transparent',
    pointerEvents: 'auto',
    cursor: 'default',
    zIndex: 10,
  };

  const channelIconOverlayStyle = {
    position: 'absolute',
    bottom: 1,
    right: 0,
    width: 110,
    height: 94,
    backgroundColor: '#fff',
    pointerEvents: 'auto',
    cursor: 'default',
    zIndex: 20,
  };

  return (
    <div className="d-flex flex-column align-items-center">
      {/* Video Player with Full Overlay */}
      <div style={{ position: 'relative', width, height, marginBottom: '20px' }}>
        <div ref={playerRef}></div>
        
        {/* Full overlay to prevent any clicking on the video */}
        <div 
          style={fullVideoOverlayStyle}
          onClick={e => e.preventDefault()}
          onContextMenu={e => e.preventDefault()}
          onDoubleClick={e => e.preventDefault()}
          onMouseDown={e => e.preventDefault()}
        />

        {/* Overlay on channel icon */}
        <div
          style={channelIconOverlayStyle}
          onClick={e => e.stopPropagation()}
          onContextMenu={e => e.preventDefault()}
        />
      </div>

      {/* External Controls */}
      <div className="bg-dark text-white p-3 rounded" style={{ width: width }}>
        {/* Seek bar with elapsed / remaining time */}
        <div className="d-flex align-items-center mb-2">
          <div className="me-2 small" style={{ width: 50, textAlign: 'left' }}>{formatTime(currentTime)}</div>
          <input
            type="range"
            className="form-range flex-grow-1"
            min="0"
            max="100"
            value={duration > 0 ? (currentTime / duration) * 100 : 0}
            onChange={handleSeek}
            disabled={!player || duration === 0}
            aria-label="Seek"
          />
          <div className="ms-2 small" style={{ width: 50, textAlign: 'right' }}>-{formatTime(Math.max(0, duration - currentTime))}</div>
        </div>

        <div className="row g-3 align-items-center">
          {/* Play/Pause Button */}
          <div className="col-auto">
            <button 
              className="btn btn-primary btn-lg"
              onClick={togglePlayPause}
              disabled={!player}
            >
              <i className={`bi ${isPlaying ? 'bi-pause-fill' : 'bi-play-fill'}`}></i>
            </button>
          </div>

          {/* Volume Control */}
          <div className="col-md-3">
            <label className="form-label small mb-1">
              <i className="bi bi-volume-up me-1"></i>Volume: {volume}%
            </label>
            <input
              type="range"
              className="form-range"
              min="0"
              max="100"
              value={volume}
              onChange={(e) => handleVolumeChange(parseInt(e.target.value))}
              disabled={!player}
            />
          </div>

          {/* Playback Speed */}
          <div className="col-md-2">
            <label className="form-label small mb-1">
              <i className="bi bi-speedometer2 me-1"></i>Speed
            </label>
            <select 
              className="form-select form-select-sm"
              value={playbackRate}
              onChange={(e) => handleSpeedChange(parseFloat(e.target.value))}
              disabled={!player}
            >
              <option value={0.25}>0.25x</option>
              <option value={0.5}>0.5x</option>
              <option value={0.75}>0.75x</option>
              <option value={1}>1x</option>
              <option value={1.25}>1.25x</option>
              <option value={1.5}>1.5x</option>
              <option value={1.75}>1.75x</option>
              <option value={2}>2x</option>
            </select>
          </div>

          {/* Quality Control */}
          <div className="col-md-2">
            <label className="form-label small mb-1">
              <i className="bi bi-gear me-1"></i>Quality
              <button 
                className="btn btn-outline-light btn-sm ms-1"
                style={{ fontSize: '10px', padding: '1px 4px' }}
                onClick={() => {
                  if (player) {
                    const qualities = player.getAvailableQualityLevels();
                    console.log('Refreshed qualities:', qualities);
                    setAvailableQualities(qualities);
                    // Only update current quality if user hasn't made a selection
                    const currentDisplayedQuality = currentQuality;
                    const actualQuality = player.getPlaybackQuality();
                    console.log('Current displayed:', currentDisplayedQuality, 'Actual:', actualQuality);
                  }
                }}
                title="Refresh quality options"
              >
                ↻
              </button>
            </label>
            <select 
              className="form-select form-select-sm"
              value={currentQuality}
              onChange={(e) => handleQualityChange(e.target.value)}
              disabled={!player}
            >
              <option value="auto">Auto</option>
              {availableQualities.map(quality => (
                <option key={quality} value={quality}>
                  {quality === 'hd2160' ? '4K' :
                   quality === 'hd1440' ? '1440p' :
                   quality === 'hd1080' ? '1080p' :
                   quality === 'hd720' ? '720p' :
                   quality === 'large' ? '480p' :
                   quality === 'medium' ? '360p' :
                   quality === 'small' ? '240p' :
                   quality === 'tiny' ? '144p' : quality}
                </option>
              ))}
            </select>
          </div>

          {/* Additional Controls */}
          <div className="col-auto">
            <button 
              className="btn btn-outline-light btn-sm me-2"
              onClick={() => player && player.seekTo(player.getCurrentTime() - 10)}
              disabled={!player}
              title="Rewind 10s"
            >
              <i className="bi bi-skip-backward"></i>
            </button>
            <button 
              className="btn btn-outline-light btn-sm"
              onClick={() => player && player.seekTo(player.getCurrentTime() + 10)}
              disabled={!player}
              title="Forward 10s"
            >
              <i className="bi bi-skip-forward"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
